import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Briefcase, MessageSquare, ShieldCheck, Users, UserSquare2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../../api';
import { liveChatApi } from '../../../api/liveChat.api';

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      height: '100%',
    }}
  >
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: color,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={22} />
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const AdminDashboardPage = ({ session = {} }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [clientsResponse, usersResponse, sessionsResponse] = await Promise.all([
          adminApi.getClients(),
          adminApi.getUsers(),
          liveChatApi.getSessions(),
        ]);
        const clientsPayload = clientsResponse?.data?.data ?? clientsResponse?.data ?? [];
        const usersPayload = usersResponse?.data?.data ?? usersResponse?.data ?? [];
        const sessionsPayload = sessionsResponse?.data?.data ?? sessionsResponse?.data ?? {};
        const sessionList = Array.isArray(sessionsPayload.sessions) ? sessionsPayload.sessions : [];
        setClients(Array.isArray(clientsPayload) ? clientsPayload : []);
        setUsers(Array.isArray(usersPayload) ? usersPayload : []);
        setSessions(sessionList);
      } catch (error) {
        setClients([]);
        setUsers([]);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const { agentCount, agentAdminCount } = useMemo(() => {
    const lower = (value) => String(value || '').toLowerCase();
    const agentCountValue = users.filter((item) => lower(item.u_role || item.role) === 'agent').length;
    const agentAdminCountValue = users.filter((item) => lower(item.u_role || item.role) === 'agentadmin').length;
    return { agentCount: agentCountValue, agentAdminCount: agentAdminCountValue };
  }, [users]);

  const openSessions = sessions.filter((item) => item.status === 'open');

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1300, mx: 'auto' }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          p: { xs: 3, md: 4 },
          mb: 4,
          background: 'linear-gradient(135deg, #111827, #1f2937)',
          color: 'white',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
          <Box>
            <Chip
              label="Admin Control Center"
              icon={<ShieldCheck size={14} />}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', mb: 2 }}
            />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Welcome, {session?.u_name || session?.name || 'Admin'}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85 }}>
              Monitor clients, teams, and live operations from one place.
            </Typography>
          </Box>
          <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/admin/clients')}
              sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
            >
              Manage Clients
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/users')}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              Manage Agents
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <StatCard icon={Briefcase} label="Clients" value={loading ? '-' : clients.length} color="#2563eb" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={Users} label="Agents" value={loading ? '-' : agentCount} color="#10b981" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={UserSquare2} label="Agent Admins" value={loading ? '-' : agentAdminCount} color="#f97316" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={MessageSquare} label="Open Chats" value={loading ? '-' : openSessions.length} color="#8b5cf6" />
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Command Shortcuts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Jump into any management workspace.
              </Typography>
            </Box>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography fontWeight={600} gutterBottom>
                  Client Operations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and manage all client accounts.
                </Typography>
                <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/admin/clients')}>
                  Open Clients
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography fontWeight={600} gutterBottom>
                  Agents & Admins
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage agent rosters and roles.
                </Typography>
                <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/admin/users')}>
                  Open Users
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography fontWeight={600} gutterBottom>
                  Live Chat Control
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Allocate chats and monitor active sessions.
                </Typography>
                <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/agent-admin/chat')}>
                  Open Live Chat
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminDashboardPage;
